import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  ButtonSmallSecondaryIcon,
  GenericButton,
  Icon,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useId } from "react";
import styled from "styled-components";
import {
  EMPTY_FIELD_RULE,
  FIELD_RULE_FORMATS,
  FIELD_RULE_KEYWORDS,
  type FieldRuleFormat,
  type FieldRuleKeyword,
  type FieldRuleRow,
  fieldRuleKind,
  fieldRuleRowError,
} from "@/utils/fieldRules";
import { SelectDropdown, type SelectOption } from "./SelectDropdown";
import { StepperInput } from "./StepperInput";
import {
  FormSection,
  HelpText,
  Input,
  Label,
  ListAddRow,
  SectionHeader,
  SectionText,
  SectionTitle,
} from "./styles";

const BUTTON = designVariables.ui.button;
const FORM = designVariables.ui.form;
const INPUT = designVariables.ui.input;

const KEYWORD_OPTIONS: readonly SelectOption<FieldRuleKeyword>[] =
  FIELD_RULE_KEYWORDS.map((k) => ({ value: k.value, label: k.label }));

const FORMAT_OPTIONS: readonly SelectOption<FieldRuleFormat>[] =
  FIELD_RULE_FORMATS.map((f) => ({ value: f, label: f }));

const VALUE_PLACEHOLDERS: Record<
  Exclude<ReturnType<typeof fieldRuleKind>, "format">,
  string
> = {
  number: "21",
  integer: "3",
  list: "ACTIVE, SUSPENDED",
};

interface FieldRulesFieldsProps {
  rows: FieldRuleRow[];
  setRows: (rows: FieldRuleRow[]) => void;
}

// Field rules render as an editable list of rows (the HTTP Source routes
// pattern): path, keyword, value; every row past the first can be removed
// with its minus button (a lone row has none), and Add appends a row once
// the last one is complete. Several keywords on one field are several rows
// sharing the path — the payload groups them.
export function FieldRulesFields({ rows, setRows }: FieldRulesFieldsProps) {
  const inputId = useId();
  const lastIndex = rows.length - 1;

  const updateRow = useCallback(
    (index: number, patch: Partial<FieldRuleRow>) => {
      setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    },
    [rows, setRows],
  );

  // Removing the only row resets the section to its empty state: one
  // blank row at the default keyword.
  const removeRow = useCallback(
    (index: number) => {
      const rest = rows.filter((_, i) => i !== index);
      setRows(rest.length > 0 ? rest : [EMPTY_FIELD_RULE]);
    },
    [rows, setRows],
  );

  const addRow = useCallback(
    () => setRows([...rows, EMPTY_FIELD_RULE]),
    [rows, setRows],
  );

  // Gates Enter-to-add only; the Add button stays enabled, since the
  // payload skips blank rows.
  const last = rows[lastIndex];
  const rowComplete =
    last.path.trim() !== "" &&
    last.value.trim() !== "" &&
    fieldRuleRowError(last) === null;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (rowComplete) addRow();
      }
    },
    [rowComplete, addRow],
  );

  const removable = rows.length > 1;

  // A ruled section of its own per Figma 16097-33747 (16099-34178).
  return (
    <FormSection>
      <SectionHeader>
        <SectionTitle>Field rules</SectionTitle>
        <SectionText>
          Narrow what a field generates without editing the schema itself. Dots
          step into nested objects and [] into an array's items, e.g.
          items[].price. Optional.
        </SectionText>
      </SectionHeader>

      <RulesList>
        {/* Column headers, not labels: a header names a column of inputs,
            and a format row's value control has no input to point at. */}
        <RuleHeader $removable={removable}>
          <Label as="span">Field path</Label>
          <Label as="span">Keyword</Label>
          <Label as="span">Value</Label>
        </RuleHeader>
        {rows.map((row, index) => (
          <RuleRowItem
            // Rows are positional (values live in state), so the index key
            // is stable enough here.
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            key={index}
            row={row}
            index={index}
            isLast={index === lastIndex}
            inputId={inputId}
            removable={removable}
            onChange={updateRow}
            onKeyDown={handleKeyDown}
            onRemove={removeRow}
          />
        ))}
        <ListAddRow>
          <GenericButton
            // GenericButton's `type` prop is the variant; the HTML type
            // attribute is set via ref.
            ref={(el) => el?.setAttribute("type", "button")}
            type="secondary"
            size={ButtonSize.Medium}
            icon={Icon.Plus}
            iconPosition="leading"
            text="Add"
            onClick={addRow}
          />
        </ListAddRow>
      </RulesList>
    </FormSection>
  );
}

// One rule row: path input, keyword select, the value control the keyword
// wants, its minus button, and the supportive line under it.
function RuleRowItem({
  row,
  index,
  isLast,
  inputId,
  removable,
  onChange,
  onKeyDown,
  onRemove,
}: {
  row: FieldRuleRow;
  index: number;
  isLast: boolean;
  inputId: string;
  removable: boolean;
  onChange: (index: number, patch: Partial<FieldRuleRow>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onRemove: (index: number) => void;
}) {
  const keyDown = isLast ? onKeyDown : undefined;
  return (
    <RuleRowGroup>
      <RuleRow $removable={removable}>
        <Input
          id={`${inputId}-path-${index}`}
          type="text"
          value={row.path}
          onChange={(e) => onChange(index, { path: e.target.value })}
          onKeyDown={keyDown}
          placeholder="address.country"
        />
        <SelectDropdown
          value={row.keyword}
          options={KEYWORD_OPTIONS}
          // A keyword change retypes the value: what was typed for one
          // rarely reads for another.
          onChange={(keyword) => onChange(index, { keyword, value: "" })}
        />
        <RuleValueControl
          row={row}
          inputId={`${inputId}-value-${index}`}
          onChange={(value) => onChange(index, { value })}
          onKeyDown={keyDown}
        />
        {removable && (
          <RemoveRuleButton
            name={row.path || String(index + 1)}
            onRemove={() => onRemove(index)}
          />
        )}
      </RuleRow>
      <RuleSupportLine row={row} isLast={isLast} />
    </RuleRowGroup>
  );
}

// The value control the keyword wants: a picker for formats, the count
// stepper for numbers (decimals and negatives allowed on bounds, digits
// only on lengths and counts), a text input for enum lists.
function RuleValueControl({
  row,
  inputId,
  onChange,
  onKeyDown,
}: {
  row: FieldRuleRow;
  inputId: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const kind = fieldRuleKind(row.keyword);
  if (kind === "format") {
    return (
      <SelectDropdown
        value={row.value as FieldRuleFormat}
        options={FORMAT_OPTIONS}
        onChange={onChange}
        placeholder="Pick a format"
      />
    );
  }
  if (kind === "number" || kind === "integer") {
    return (
      <StepperInput
        id={inputId}
        label={`${row.keyword} value`}
        value={row.value}
        onChange={onChange}
        placeholder={VALUE_PLACEHOLDERS[kind]}
        decimal={kind === "number"}
      />
    );
  }
  return (
    <Input
      id={inputId}
      type="text"
      value={row.value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={VALUE_PLACEHOLDERS[kind]}
    />
  );
}

function RemoveRuleButton({
  name,
  onRemove,
}: {
  name: string;
  onRemove: () => void;
}) {
  return (
    <ButtonSmallSecondaryIcon
      // GenericButton derivatives drop type/aria props; both are set on
      // the real <button> via ref (type defaults to "submit" inside the
      // form).
      ref={(el) => {
        el?.setAttribute("type", "button");
        el?.setAttribute("aria-label", `Remove rule ${name}`);
      }}
      icon={Icon.Minus}
      onClick={onRemove}
    />
  );
}

// The supportive line under a row: its error, or the list hint on the row
// being typed. Nothing otherwise — rows space themselves (RuleRowGroup),
// so no placeholder line is needed.
function RuleSupportLine({
  row,
  isLast,
}: {
  row: FieldRuleRow;
  isLast: boolean;
}) {
  const error = fieldRuleRowError(row);
  if (error) return <RuleError>{error}</RuleError>;
  const listHint = isLast && fieldRuleKind(row.keyword) === "list";
  return listHint ? <HelpText>Comma-separated values.</HelpText> : null;
}

// Columns per Figma 16099-34179: path and value stretch evenly around the
// fixed keyword select. The minus button gets a slot of its own only while
// the rows show one, so a lone row spans the full width like the mock.
const KEYWORD_WIDTH = 200;
const columns = (removable: boolean) =>
  `minmax(0, 1fr) ${KEYWORD_WIDTH}px minmax(0, 1fr)${
    removable ? ` ${BUTTON.s.size}px` : ""
  }`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RuleHeader = styled.div<{ $removable: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => columns(props.$removable)};
  gap: ${FORM.gapitems}px;
  margin-bottom: ${INPUT.gaplabel}px;
`;

// Row + its optional supportive line; consecutive rows keep the form's
// item gap between them, the Add row below brings its own top padding.
const RuleRowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;

  & + & {
    margin-top: ${FORM.gapitems}px;
  }
`;

const RuleRow = styled.div<{ $removable: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => columns(props.$removable)};
  align-items: center;
  gap: ${FORM.gapitems}px;

  > ${Input} {
    min-width: 0;
  }
`;

const RuleError = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.statusError};
`;
